import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "../data/site";

export const Services = ({ limit, singlePage, services: servicesProp }) => {
  const source = servicesProp || SERVICES;
  const list = limit ? source.slice(0, limit) : source;

  return (
    <section data-testid="services-section" className="scroll-mt-28 relative py-24 md:py-32 bg-slate-50">
      <div className="absolute inset-0 dot-pattern opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold flex items-center gap-2">
              <span className="w-8 h-px bg-amber-500" />
              Capabilities
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-bold text-navy-900 tracking-[-0.03em] leading-[1]">
              Engineered end-to-end,
              <br />
              <span className="text-amber-500">delivered with precision.</span>
            </h2>
          </motion.div>
          <Link
            to="/services"
            data-testid="services-view-all"
            className={`group inline-flex items-center gap-2 text-sm text-navy-900 font-semibold tracking-tight hover:gap-3 transition-all ${singlePage ? "hidden" : ""}`}
          >
            View all capabilities
            <ArrowUpRight className="w-4 h-4 text-amber-600 group-hover:rotate-45 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              className="lift-card group relative bg-white border border-slate-200 rounded-2xl p-7 md:p-8 overflow-hidden"
              data-testid={`service-card-${s.slug}`}
            >
              <div className="absolute top-5 right-5 text-[11px] font-mono text-slate-300 tracking-tight">
                /0{i + 1}
              </div>

              <div className="w-14 h-14 rounded-2xl bg-navy-900 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-navy-900 transition-all">
                <s.icon className="w-7 h-7" />
              </div>

              <h3 className="mt-7 font-display text-2xl font-bold text-navy-900 tracking-tight leading-tight">
                {s.title}
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">
                {s.short}
              </p>

              {singlePage ? (
                <a
                  href="#contact"
                  data-testid={`service-learn-${s.slug}`}
                  className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-navy-900 group-hover:text-amber-600 transition-colors"
                >
                  Start a conversation
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform" />
                </a>
              ) : (
                <Link
                  to="/services"
                  data-testid={`service-learn-${s.slug}`}
                  className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-navy-900 group-hover:text-amber-600 transition-colors"
                >
                  Learn more
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform" />
                </Link>
              )}

              <div className="absolute bottom-0 left-0 right-0 h-1 bg-amber-500 scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
